import React, { BaseSyntheticEvent, useContext, useEffect, useState } from 'react';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';

import { ReactComponent as ExpandMore } from '../../../../../assets/icons/expand-more.svg';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import StyledMenu, { getSelectedIndex, IMenuItem } from '../../../../../shared/components/StyledMenu/StyledMenu';
import { MENU_DIRECTION_RIGHT } from '../../../../../shared/constants/constants';
import { LANGUAGES_KEYS, LOKALISE_KEYS } from '../../../../../shared/constants/i18n';
import { INTERCOM_SET_LANGUAGE_EVENT } from '../../../../../shared/constants/intercom';
import { CLICK_ACTION, SELECT_LANGUAGE } from '../../../../../shared/constants/tagManager';
import {
  UPDATE_USER_LOCALE_ERROR_TOAST,
  UPDATE_USER_LOCALE_SUCCESS_TOAST
} from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IToast } from '../../../../../shared/interfaces/toast';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import UpdateUserAttributes from '../../../../../shared/services/httpUpdateUserAttributes';
import { TRANSLATIONS_SETTINGS } from '../../../../../shared/translations/settings';
import { USER_ATTRIBUTES } from '../../../../../shared/types/businessDataTypes';
import { SupportedISOCode } from '../../../../../shared/types/i18n';

import './LanguageSelector.scss';

interface ILanguageSelectorProps {
  handleOpenToast: (toast: IToast) => void;
}

const LanguageSelector = ({ handleOpenToast }: ILanguageSelectorProps) => {
  const { locale, setUserLocale } = useContext(CompanyContext);

  const [languageName, setLanguageName] = useState('');
  const [isoCode, setIsoCode] = useState<SupportedISOCode>(locale as SupportedISOCode);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t, i18n } = useTranslation();
  const { update, trackEvent } = useIntercom();

  useEffect(() => {
    document.title = t('site_title');
    setLanguageName(LANGUAGES_KEYS[i18n.resolvedLanguage as SupportedISOCode]);
    moment.locale(i18n.resolvedLanguage);
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    i18n.changeLanguage(locale);
    moment.locale(locale);
  }, [locale]);

  const handleClick = (event: BaseSyntheticEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setIntercomData = (countryCode: string) => {
    const locale = countryCode === 'pt' ? 'pt-BR' : countryCode;
    update({ languageOverride: locale });
    const metadata = { locale };
    trackEvent(INTERCOM_SET_LANGUAGE_EVENT, metadata);
  };

  const handleSelectLanguage = (event: BaseSyntheticEvent<PointerEvent, HTMLLIElement, HTMLLIElement>) => {
    const selectedIndex = getSelectedIndex(event);
    setIsoCode(LOKALISE_KEYS[selectedIndex].isoCode);
    setLanguageName(LOKALISE_KEYS[selectedIndex].lokaliseKey);
    setAnchorEl(null);
  };

  const changeLanguage = async () => {
    try {
      if (isoCode) {
        setLoading(true);
        const userAttribute: USER_ATTRIBUTES = {
          name: 'locale',
          value: isoCode
        };
        await UpdateUserAttributes.update({ attributes: [userAttribute] });
        i18n.changeLanguage(isoCode);
        moment.locale(isoCode);
        setUserLocale(isoCode);
        setIntercomData(isoCode);
        TagManagerService.pushEvent(CLICK_ACTION, SELECT_LANGUAGE, { language: isoCode });
        handleOpenToast(UPDATE_USER_LOCALE_SUCCESS_TOAST);
      }
    } catch (error) {
      handleOpenToast(UPDATE_USER_LOCALE_ERROR_TOAST);
    } finally {
      setLoading(false);
    }
  };

  const menuItems: IMenuItem[] = LOKALISE_KEYS.map<IMenuItem>((l) => ({
    label: t(l.lokaliseKey),
    action: handleSelectLanguage
  }));

  return (
    <div className="settings-account-card">
      <div className="title">{t(TRANSLATIONS_SETTINGS.languageSelector.title)}</div>
      <div className="language-selector-ne">
        <div className="language-selector-row">
          <button className="language-selector" onClick={handleClick}>
            <div className="language-label"> {t(TRANSLATIONS_SETTINGS.languageSelector.title)}</div>
            <div className="language-input">
              {t(languageName)} <ExpandMore className="icon" data-testid="expand-more-icon" />
            </div>
          </button>
        </div>
        <StyledButtonNE
          buttonType="secondary"
          classes="language-selector-button"
          buttonSize="md"
          action={changeLanguage}
          loading={loading}
        >
          {t(TRANSLATIONS_SETTINGS.save)}
        </StyledButtonNE>
      </div>
      <StyledMenu
        anchorEl={anchorEl}
        handleClose={handleClose}
        menuItems={menuItems}
        anchorOrigin={MENU_DIRECTION_RIGHT.anchor}
        transformOrigin={MENU_DIRECTION_RIGHT.transform}
      />
    </div>
  );
};

export default LanguageSelector;
